import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

type DocumentSectionProps = {
  title: string;
  lastUpdated: string;
  appName: string;
  extraMeta?: ReactNode;
  children: ReactNode;
};

export function DocumentSection({
  title,
  lastUpdated,
  appName,
  extraMeta,
  children,
}: DocumentSectionProps) {
  return (
    <>
      <section className="border-b border-border/60 px-6 py-16 md:px-12">
        <div className="mx-auto max-w-3xl">
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-muted-foreground">
            Legal
          </p>
          <h1 className="mt-4 font-serif text-3xl font-light leading-tight tracking-tight text-foreground md:text-4xl">
            {title}
          </h1>
          <ul className="mt-6 space-y-2 text-sm text-muted-foreground">
            <li>
              <span className="font-medium text-foreground/80">Last updated:</span>{" "}
              {lastUpdated}
            </li>
            <li>
              <span className="font-medium text-foreground/80">App name:</span> {appName}
            </li>
            {extraMeta}
          </ul>
        </div>
      </section>
      <section className="px-6 py-16 md:px-12 md:py-20">
        <article className={cn("mx-auto max-w-3xl", documentBodyClass)}>{children}</article>
      </section>
    </>
  );
}

export const documentBodyClass =
  "space-y-6 text-sm leading-relaxed text-foreground/90 md:text-base [&_strong]:font-medium [&_strong]:text-foreground";

export function DocH2({ children }: { children: ReactNode }) {
  return (
    <h2 className="mt-12 border-t border-border/60 pt-10 font-serif text-xl font-light tracking-tight text-foreground first:mt-0 first:border-t-0 first:pt-0 md:text-2xl">
      {children}
    </h2>
  );
}

export function DocH3({ children }: { children: ReactNode }) {
  return <h3 className="mt-6 text-base font-medium text-foreground">{children}</h3>;
}

export function DocP({ children }: { children: ReactNode }) {
  return <p>{children}</p>;
}

export function DocHr() {
  return <hr className="border-border/60" />;
}

export function DocUl({ children }: { children: ReactNode }) {
  return <ul className="list-disc space-y-2 pl-5">{children}</ul>;
}

export function DocOl({ children }: { children: ReactNode }) {
  return <ol className="list-decimal space-y-2 pl-5">{children}</ol>;
}

export function DocTable({ children }: { children: ReactNode }) {
  return (
    <div className="overflow-x-auto rounded-md border border-border/60">
      <table className="w-full min-w-[280px] border-collapse text-sm [&_th]:border-b [&_th]:border-border/60 [&_th]:bg-muted/30 [&_th]:px-4 [&_th]:py-3 [&_th]:text-left [&_th]:font-medium [&_th]:text-foreground [&_td]:border-b [&_td]:border-border/60 [&_td]:px-4 [&_td]:py-3 [&_td]:align-top [&_td]:text-muted-foreground [&_tr:last-child_td]:border-b-0">
        {children}
      </table>
    </div>
  );
}

export function DocMetaItem({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <li>
      <span className="font-medium text-foreground/80">{label}</span> {children}
    </li>
  );
}

export function DocCode({ children }: { children: ReactNode }) {
  return (
    <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-[0.85em] text-foreground">
      {children}
    </code>
  );
}

export function DocLink({
  href,
  children,
}: {
  href: string;
  children: ReactNode;
}) {
  return (
    <a
      href={href}
      className="text-foreground underline decoration-border underline-offset-4 transition-colors hover:text-foreground/80"
    >
      {children}
    </a>
  );
}

export function DocEm({ children }: { children: ReactNode }) {
  return <em className="text-muted-foreground not-italic">{children}</em>;
}
